import { useAuthStore } from '@/stores/auth-store';
import { isInIframeContext } from '@/lib/iframe-child-bridge';

export interface ReferralAccess {
  /** Show the referral entry points: pill, user-menu card, sidebar item. */
  entryEnabled: boolean;
  /** Allow the /app/s/referral page. Flag-INDEPENDENT — stays URL-reachable for
   *  production testing; only the engine being configured, a paid subscription,
   *  and a completed onboarding checklist gate it. */
  pageAccessible: boolean;
  /** Subscription data not yet resolved (company === null). Callers must defer:
   *  hide entry points, and do NOT redirect the page until this clears. */
  loading: boolean;
}

/**
 * Single source of truth for who can see/reach the referral feature.
 * Paid = active subscription, not on trial, not on the free plan.
 * Also gated on onboarding-complete (see onboardingComplete below).
 *
 * NOTE (verify): payment.isActive is expected to be false for past_due/canceled
 * accounts so lapsed paid accounts also lose access; the !isTrial term guards the
 * trial case regardless of isActive.
 */
export function useReferralAccess(): ReferralAccess {
  const company = useAuthStore((s) => s.company);

  const flagEnabled = import.meta.env.VITE_ENABLE_REFERRAL_ENGINE === 'true';
  const apiConfigured = Boolean(import.meta.env.VITE_REFERRAL_API_URL);
  // "Refer & Earn" is an OptiSigns-branded program — hidden entirely on white-label accounts
  // (no entry points, page not reachable).
  const isWhiteLabel = !!company?.brandingData?.isWhiteLabel;

  // company is null until auth/subscription data resolves.
  const loading = company === null;

  const payment = company?.payment;
  const isPaidSubscriber =
    Boolean(payment?.isActive) && !payment?.isTrial && payment?.planFeature !== 'free';

  // Onboarding gate: getStarted.completed is set only when the user dismisses the
  // get-started checklist, and that X button only appears once the required
  // onboarding steps are done — so it reliably signals a completed onboarding.
  const onboardingComplete = Boolean(company?.getStarted?.completed);

  return {
    entryEnabled:
      flagEnabled && !isInIframeContext && isPaidSubscriber && !isWhiteLabel && onboardingComplete,
    pageAccessible:
      apiConfigured &&
      !isInIframeContext &&
      isPaidSubscriber &&
      !isWhiteLabel &&
      onboardingComplete,
    loading,
  };
}
